import React from 'react';
import { Grid, useTheme } from '@mui/material';
import { currencyFormatter } from 'helpers/formatHelper';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import Flex from 'ui/Flex/Flex';
import { RetrospectiveSalaryMassData } from '../helpers/retrospectiveData';
import {
    StyBlackBoldText,
    StyPrimaryBoldText,
    StyText,
    StyPrimaryText,
} from '../helpers/StyTypography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { formatPercentageNumber } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/dataHelpers';
import { useTranslation } from 'react-i18next';

export const SalaryMassTotals = ({
    data,
    exercice,
}: {
    data: RetrospectiveSalaryMassData;
    exercice: number;
}): React.ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const total = data.brutTotal + data.employerCostsTotal;
    const salaryMassEvolutionAmount =
        data.salaryMassCurrentYear - data.salaryMassPreviousYear;

    const salaryMassPercentage =
        data.salaryMassPreviousYear === 0
            ? 0
            : (salaryMassEvolutionAmount / data.salaryMassPreviousYear) * 100;
    return (
        <StyPaper
            sx={{
                height: '100%',
                padding: '1rem',
                marginBottom: '1rem',
            }}
        >
            <Grid
                container
                gap={2}
                height="100%"
                display="flex"
                justifyContent="space-around"
                alignItems="center"
            >
                <Grid item xs={12}>
                    <Flex justifyContent="space-between" height="fit-content">
                        <StyBlackBoldText>
                            {t(
                                'humanResources.retrospective.salaryMass.loadedBrutTotal',
                            )}
                        </StyBlackBoldText>
                        <StyPrimaryBoldText>
                            {currencyFormatter(total)}
                        </StyPrimaryBoldText>
                    </Flex>
                </Grid>
                <Grid item xs={12}>
                    <Flex justifyContent="space-between">
                        <StyText>
                            {t(
                                'humanResources.retrospective.salaryMass.loadedTotal',
                            )}
                        </StyText>
                        <StyPrimaryText>
                            {currencyFormatter(data.brutTotal)}
                        </StyPrimaryText>
                    </Flex>
                </Grid>
                <Grid item xs={12}>
                    <Flex justifyContent="space-between">
                        <StyText>
                            {t(
                                'humanResources.retrospective.salaryMass.employerCostTotal',
                            )}
                        </StyText>
                        <StyPrimaryText>
                            {currencyFormatter(data.employerCostsTotal)}
                        </StyPrimaryText>
                    </Flex>
                </Grid>
                <Grid item xs={12}>
                    <Flex justifyContent="space-between">
                        <StyBlackBoldText>
                            {t(
                                'humanResources.retrospective.salaryMass.salaryMassEvolution',
                                {
                                    year: exercice - 1,
                                },
                            )}
                        </StyBlackBoldText>
                        <Flex justifyContent="end" gap={2}>
                            <StyPrimaryBoldText>
                                {currencyFormatter(salaryMassEvolutionAmount)}
                            </StyPrimaryBoldText>
                            <StyPrimaryBoldText>
                                {formatPercentageNumber(salaryMassPercentage)}
                            </StyPrimaryBoldText>
                            {salaryMassPercentage > 0 ? (
                                <TrendingUpIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.red,
                                    }}
                                />
                            ) : (
                                <TrendingDownIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.green,
                                    }}
                                />
                            )}
                        </Flex>
                    </Flex>
                </Grid>
            </Grid>
        </StyPaper>
    );
};
